import { useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { PostRequest } from "../../../Utils/hooks/ApiHandler";
import { toast } from "sonner";
import { Button, Modal } from "react-bootstrap";
import { Plus } from "lucide-react";
import { IconBell, IconTrash } from "@tabler/icons-react";
import Select from "react-select";
import useLocalStorageState from "use-local-storage-state";
import nodata from "../../../Utils/assets/img/nodata.png";
import styles from "./Preferences.module.css";

// Import FontAwesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSmile,
  faCommentDots,
  faBook,
  faRobot,
  faHandshake,
  faBullhorn,
  faBullseye,
  faIdBadge,
  faBell,
} from "@fortawesome/free-solid-svg-icons";
import { AuthorizationCard } from "../../../pages/AuthorizationCard";

// Define the preference items with FontAwesome icons
const preferenceItems = [
  {
    subtitle: "DoubleSpring",
    title: "Forms",
    description:
      "You can easily customize your forms by adding or removing custom fields in both Leads and Deals forms.",
    bgColor: "#788d22ff",
    icon: <FontAwesomeIcon icon={faSmile} size="1x" color="white" />,
    path: "/preferences/form",
  },
  {
    subtitle: "DoubleSpring1",
    title: "Notification Template",
    description:
      "Create and configure custom WhatsApp, SMS, and email notification templates for your Leads and Deals.",
    icon: <FontAwesomeIcon icon={faCommentDots} size="1x" color="white" />,
    bgColor: "#198f9cff",
    path: "/preferences/notificationTemplates",
  },
  {
    subtitle: "DoubleSpring2",
    title: "SMS Templates",
    description:
      "Seamlessly integrate SMS services to receive real-time notifications and updates for all events.",
    icon: <FontAwesomeIcon icon={faCommentDots} size="1x" color="white" />,
    bgColor: "#d83160ff",
    path: "/preferences/sms-templates",
  },
  {
    subtitle: "DoubleSpring3",
    title: "WhatsApp API",
    description:
      "Integrate your business WhatsApp with the CRM to run campaigns and enable alert notifications.",
    icon: <FontAwesomeIcon icon={faRobot} size="1x" color="white" />,
    bgColor: "#30e524ff",
    path: "/preferences/whatsapp-api",
  },
  {
    subtitle: "DoubleSpring4",
    title: "Knowledge Base",
    description:
      "Upload your knowledge base documents, brochures, and price lists for reference and easy sharing with your team.",
    icon: <FontAwesomeIcon icon={faBook} size="1x" color="white" />,
    bgColor: "#a81047ff",
    path: "/preferences/knowledge-base",
  },
  {
    subtitle: "DoubleSpring5",
    title: "Leads Visibility",
    description:
      "Set lead visibility rules and decide whether leads are visible to sales teams or specific executives.",
    icon: <FontAwesomeIcon icon={faCommentDots} size="1x" color="white" />,
    bgColor: "#514fc5ff",
    path: "/preferences/leads-visibility",
  },
  {
    subtitle: "DoubleSpring6",
    title: "Terms & Conditions",
    description:
      "Set up your Quotation and Sales Order terms and conditions here, which will automatically populate during creation.",
    icon: <FontAwesomeIcon icon={faHandshake} size="1x" color="white" />,
    bgColor: "#a18636ff",
    path: "/preferences/terms-conditions",
  },
  {
    subtitle: "DoubleSpring7",
    title: "Announcement",
    description:
      "Create announcement rules to broadcast important updates to all your customers.",
    icon: <FontAwesomeIcon icon={faBullhorn} size="1x" color="white" />,
    bgColor: "#11959aff",
    path: "/preferences/announcement",
  },

  {
    subtitle: "DoubleSpring8",
    title: "Set Target",
    description:
      "Define and assign monthly targets to executives to track and monitor performance effectively.",
    icon: <FontAwesomeIcon icon={faBullseye} size="1x" color="white" />,
    bgColor: "#573aacff",
    path: "/preferences/set-target",
  },
  {
    subtitle: "DoubleSpring9",
    title: "Prefix",
    description:
      "Configure prefixes and starting series for contacts, leads, deals, quotations, and additional records.",
    icon: <FontAwesomeIcon icon={faIdBadge} size="1x" color="white" />,
    bgColor: "#cf2e54ff",
    path: "/preferences/prefix",
  },
  {
    subtitle: "DoubleSpring10",
    title: "Reminders",
    description:
      "Enable automatic reminders before and after payment due dates to ensure timely follow-ups.",
    icon: <FontAwesomeIcon icon={faBell} size="1x" color="white" />,
    bgColor: "#a33ba0ff",
    path: "",
  },
];

export const Preferences = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useLocalStorageState("customer");
  const customer_id = customer?.customer_details?.customer_id;
  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;
  const headers = {
    Authorization: token,
    Authentication: rf,
  };
  const extras = { customer, setCustomer, rf, navigate };
  const [searchBarValue,setSearchBarValue]=useState("")
  // permission setup for preference
  const permission = customer.customer_details?.permission;
  const user_type = Number(customer.customer_details?.user_type);
  const settingsPermission = permission?.filter(
    (c) => c.module_name === "setting",
  );
  // view permission for preference
  const preferenceView =
    user_type !== 1
      ? Boolean(
          Number(
            settingsPermission?.filter(
              (c) => c.feature_name === "preference",
            )[0]?.view,
          ),
        )
      : true;
  const preferenceCreate =
    user_type !== 1
      ? Boolean(
          Number(
            settingsPermission?.filter(
              (c) => c.feature_name === "preference",
            )[0]?.create,
          ),
        )
      : true;

  // Reminder modal states
  const [reminderModal, setReminderModal] = useState(false);
  const [beforeDueReminders, setBeforeDueReminders] = useState([
    { id: 1, days: "1" },
  ]);
  const [afterDueReminders, setAfterDueReminders] = useState([
    { id: 1, days: "1" },
  ]);

  const dayOptions = [
    { value: "1", label: "1 day" },
    { value: "2", label: "2 days" },
    { value: "3", label: "3 days" },
    { value: "4", label: "4 days" },
  ];

  const addReminder = (type) => {
    const reminders =
      type === "before" ? beforeDueReminders : afterDueReminders;
    if (reminders.length < 2) {
      const newId = reminders[reminders.length - 1]?.id + 1;
      const newReminder = { id: newId, days: "1" };
      type === "before"
        ? setBeforeDueReminders([...reminders, newReminder])
        : setAfterDueReminders([...reminders, newReminder]);
    }
  };

  const deleteReminder = (type, id) => {
    if (type === "before") {
      setBeforeDueReminders(beforeDueReminders.filter((r) => r.id !== id));
    } else {
      setAfterDueReminders(afterDueReminders.filter((r) => r.id !== id));
    }
  };

  const updateReminderDays = (type, id, days) => {
    if (type === "before") {
      setBeforeDueReminders(
        beforeDueReminders.map((r) => (r.id === id ? { ...r, days } : r)),
      );
    } else {
      setAfterDueReminders(
        afterDueReminders.map((r) => (r.id === id ? { ...r, days } : r)),
      );
    }
  };

  const handleSubmitReminder = async () => {
    if (!preferenceCreate) {
      toast.warning("You are not authorized for this action!");
      return;
    }
    const payload = {
      customer_id: customer_id,
      remainderbefore: beforeDueReminders.map((c) => parseInt(c.days)),
      remainderafter: afterDueReminders.map((c) => parseInt(c.days)),
    };

    const res = await PostRequest(
      `preference/paymentRemainder/`,
      payload,
      false,
      { headers },
      extras,
    );

    if (!res.success) {
      toast.error(res.message);
      return;
    }
    toast.success("Payment reminder saved!");
    setReminderModal(false);
  };

  const getReminderLabel = (index) => {
    return index === 0 ? "First Reminder" : "Second Reminder";
  };

  return (
    <div className="container mb-5">
      {/* <div className="d-flex flex-column text-start maindiv">
        <h2 className="h4 mb-1">Preferences</h2>
        <span className="text-muted mb-2" style={{ fontSize: "0.9rem" }}>
          You can manage certificates, notification templates, GST setting,
          payment gateway, etc.
        </span>
        <hr
          className=" mb-4"
          style={{ borderTop: "0.5px solid #6c757d", opacity: "0.3", marginTop: "0.7rem" }}
        />
      </div> */}

      <div className="mb-3 d-flex justify-content-between align-items-end">
        <div style={{ lineHeight: "0.8rem" }}>
          <h6
            // className="addEnquiryHead"
            style={{ fontWeight: "500", color: "black", fontSize: "1.0rem" }}
          >
            Preferences
          </h6>
          <p className="text-muted mb-0 small" style={{ color: "#c7c7c7" }}>
            You can manage certificates, notification templates, GST setting,
            payment gateway, etc.
          </p>
        </div>
           {/* search bar */}
                  <div className=" position-relative">
                    <input
                      type="text"
                      style={{ fontSize: "12px", paddingLeft: "36px", height: "35px" }}
                      className="form-control "
                      value={searchBarValue}
                      onChange={(e) => {
                        setSearchBarValue(e.target.value);
                       
                      }}
                   
                    />
                    <i
                      class="fa-solid fa-magnifying-glass"
                      style={{
                        position: "absolute",
                        marginTop: "-26px",
                        zIndex: 1,
                        marginLeft: "13px",
                      }}
                    ></i>
                 
                  </div>
      </div>
      <hr
        className="mt-2 mb-4"
        style={{
          borderTop: "0.5px solid #6c757d",
          opacity: "0.3",
        }}
      />

      {preferenceView ? (
        <div
          className="row gx-3 gy-3 justify-start row1 "
           style={{
           scrollbarColor:" #aaafb3 #f3f3f3",
           scrollbarWidth: "thin",
           height: "75vh",
           overflow: "scroll",
        }}
        >
          {(() => {
            const filteredData = preferenceItems.filter((app) => {
              if (!searchBarValue) return true;

              const searchTerm = searchBarValue.toLowerCase();
              return (
                app.title.toLowerCase().includes(searchTerm) ||
                app.subtitle.toLowerCase().includes(searchTerm)
              );
            });
            return filteredData.length === 0 ? (
              <div className="w-100 text-center mt-4">
                <img
                  src={nodata}
                  alt=""
                  style={{ width: "68vh", marginTop: "10%" }}
                />
              </div>
            ) : (
              filteredData?.map((item, index) => (
                <div
                  className="col-12 col-sm-6 col-md-6 col-lg-6 col-xl-4 col-xxl-3 d-flex"
                  key={index}
                >
                  <Link
                    to={item.path}
                    onClick={() =>
                      item.title === "Reminders" && setReminderModal(true)
                    }
                    className="text-decoration-none text-dark"
                  >
                    <div className={styles.customcardpreference}>
                      <div
                        className={`card-body d-flex flex-column ${styles.mainpadding}`}
                        style={{ minHeight: "15.5rem" }}
                      >
                        {/* <p className={styles.timep}> */}
                        {/* FEW SECONDS */}
                        {/* </p> */}
                        {/* <p className={styles.stylehorizontal}></p> */}

                        {/* Replace img with FontAwesomeIcon */}
                        {/* <div className={styles.cardlogopreference}>{item.icon}</div> */}

                        <div
                          className={styles.cardlogopreference}
                          style={{
                            backgroundColor: item.bgColor,
                            borderRadius: "50%",
                            padding: "10px",
                          }}
                        >
                          {item.icon}
                        </div>

                        {/* <p className={styles.subhead2}> */}
                        {/* {item.subtitle} */}
                        {/* </p> */}

                        {/* <h6 className={styles.title4}>{item.title}</h6> */}
                        <h2
                          // style={{
                          //   fontWeight: 600,
                          //   color: "#2e384e",
                          //   fontSize: "1.2rem",
                          //   textAlign: "start",
                          //   paddingTop: "6px",
                          // }}
                          className={styles.title4}
                        >
                          {item.title}
                        </h2>
                        <div className="tooltip-preference w-100">
                          <p className={styles.ptag1}>{item.description}</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))
            );
          })()}
        </div>
      ) : (
        <AuthorizationCard></AuthorizationCard>
      )}

      {/* Reminder Modal */}
      <Modal
        show={reminderModal}
        centered
        onHide={() => setReminderModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex gap-3 align-items-center justify-content-center me-2">
            <IconBell stroke={1} size={50} style={{ stroke: "#009efb" }} />
            <div className="d-flex flex-column">
              <p className="fw-400 fs-17 mb-2 text-dark">Payment Reminders</p>
              <p
                className="text-muted fs-12"
                style={{ fontWeight: 300, lineHeight: "normal" }}
              >
                Setup auto send payment reminders before and after due date. Max
                2 reminders each.
              </p>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Reminder Before Due Date */}
          <p className="mb-2 text-black fs-15 fw-400">
            Reminder Before Due Date
          </p>
          {beforeDueReminders.map((reminder, index) => (
            <div
              key={reminder.id}
              style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}
            >
              <label className="form-label mb-1 px-3 pt-3">
                {getReminderLabel(index)}
              </label>
              <div className="d-flex align-items-center justify-content-center gap-3 mb-3 px-3 pb-3">
                {" "}
                <div className="flex-grow-1">
                  <Select
                    value={dayOptions.find(
                      (option) => option.value === reminder.days,
                    )}
                    onChange={(selectedOption) =>
                      updateReminderDays(
                        "before",
                        reminder.id,
                        selectedOption.value,
                      )
                    }
                    options={dayOptions}
                  />
                </div>
                {beforeDueReminders.length > 1 && (
                  <button
                    onClick={() => deleteReminder("before", reminder.id)}
                    className="btn btn-outline-danger btn-sm"
                  >
                    <IconTrash size={18} />
                  </button>
                )}
                {beforeDueReminders.length < 2 && (
                  <button
                    className="btn btn-default-outline"
                    onClick={() => addReminder("before")}
                  >
                    <div className="d-flex gap-1 justify-content-center align-items-center">
                      <Plus size={18} />
                      Add More
                    </div>
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Reminder After Due Date */}

          <p className="mb-2 text-black fs-15 mt-2 fw-400">
            Reminder After Due Date
          </p>
          {afterDueReminders.map((reminder, index) => (
            <div
              key={reminder.id}
              // className="d-flex align-items-center gap-3 mb-3 p-3"
              style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}
            >
              {" "}
              <label className="form-label mb-1 px-3 pt-3">
                {getReminderLabel(index)}
              </label>
              <div className="d-flex align-items-center justify-content-center gap-3 mb-3 px-3 pb-3">
                <div className="flex-grow-1">
                  <Select
                    value={dayOptions.find(
                      (option) => option.value === reminder.days,
                    )}
                    onChange={(selectedOption) =>
                      updateReminderDays(
                        "after",
                        reminder.id,
                        selectedOption.value,
                      )
                    }
                    options={dayOptions}
                  />
                </div>
                {afterDueReminders.length > 1 && (
                  <button
                    onClick={() => deleteReminder("after", reminder.id)}
                    className="btn btn-outline-danger btn-sm"
                  >
                    <IconTrash size={18} />
                  </button>
                )}
                {afterDueReminders.length < 2 && (
                  <button
                    className="btn btn-default-outline"
                    onClick={() => addReminder("after")}
                  >
                    <div className="d-flex gap-1 justify-content-center align-items-center">
                      <Plus size={18} />
                      Add More
                    </div>
                  </button>
                )}{" "}
              </div>
            </div>
          ))}
        </Modal.Body>

        <Modal.Footer>
          <Button className="btn btn-default" onClick={handleSubmitReminder}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
